import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { WerkstattBranchHeader } from "../components/werkstatt/WerkstattBranchHeader";
import { DetailModal } from "../components/DetailModal";
import { stepUuid } from "../lib/stepUuid";
import { cellBboxPct, detectedCellBbox, type DetectedGrid, type FieldName, type BboxPct } from "../lib/cellBbox";
import { spatialFromData, spatialCellBboxPct } from "../lib/expenseSpatial";
import { expenseCellBboxPct, type ExpenseFieldName } from "../lib/expenseCellBbox";

/**
 * Grid-Audit (2026-06-15, überarbeitet) — Highlight-Qualität pro Zelle als
 * BASELINE-Messung, ein Bild, gemessen an der ECHTEN Highlight-Geometrie.
 *
 * EIN Scan-Bild mit zwei übereinanderliegenden Ebenen:
 *  1. Audit-Raster (Standbild, wie gehabt): alle Zell-Boxen, eingefärbt nach
 *     meinem Urteil (Q1/Q2). Geometrie aus den Produktionsfunktionen
 *     (detectedCellBbox ?? cellBboxPct mit dem 31-Zeilen/4-Spalten-Gate von
 *     PdfWithHighlights; spatialCellBboxPct ?? expenseCellBboxPct) — die Box,
 *     die ich einfärbe, IST die Box, die tatsächlich highlightet wird.
 *  2. Live-Highlight (übergelagert): beim Hover (über eine Box oder eine
 *     Tabellenzeile) erscheint genau das orange Kästchen aus dem echten
 *     Detail-Screen (.pdf-overlay-cell.active-Stil) auf der Zelle — ohne Zoom,
 *     ohne Flackern. So siehst Du, ob das echte Highlight den Wert vollständig
 *     trifft (z. B. ob bei „12:00" nur „12" markiert ist).
 *
 * Quelle-Badge pro Zelle (detected/heuristic/spatial) zeigt, ob das echte
 * Highlight das erkannte Raster nutzt oder auf die Heuristik zurückfällt —
 * das ist das Signal für den Spalten-Versatz (#29).
 *
 * Jede Zelle hat eine IMMUTABLE ID (stepUuid, deterministisch aus
 * card+phase+zelle) — festes Muster, auflösbar: der Resolve-Input findet jede
 * ID der geladenen Karte; karten­übergreifend rechne ich sie aus den
 * persistierten Kartendaten neu nach.
 *
 * v1-Heuristik (transparent): Q1 = von1 > 22 % ⇒ Spalten-Versatz (Stunden) ·
 * symb separat (rechte Bandlage) · Q2 = Box-Breite < 4-5 % ⇒ evtl. abgeschnitten.
 * Präzision danach: OCR-Readback (Q2) + #29-Referenzraster (Q1).
 */

type Kind = "timesheet" | "expense";
const TS_FIELDS: FieldName[] = ["von1", "bis1", "von2", "bis2", "symb"];
const EXP_FIELDS: ExpenseFieldName[] = ["datum", "abfahrt", "rueckkehr"];

type Source = "detected" | "heuristic" | "spatial" | "—";
interface AuditCell {
  uuid: string;
  rowLabel: string;
  field: string;
  value: string;
  box: BboxPct | null;            // PROZENT (0–100), Produktions-Konvention
  source: Source;
  q1: "ok" | "FALSCHE SPALTE" | "—";
  q2: "ok" | "BOX ZU SCHMAL" | "—";
}

const DEMO: { id: string; label: string; kind: Kind }[] = [
  { id: "a4d0e432-f875-4b13-9592-6f36b0ee6d31", label: "Std · (ohne Name)", kind: "timesheet" },
  { id: "0e4992c5-b688-40eb-a18b-521b2925f3da", label: "Std · Zorzi", kind: "timesheet" },
  { id: "ad14c2c0-3ac3-457e-9e95-91e6aea5ed95", label: "Std · Zirkl", kind: "timesheet" },
  { id: "42655369-b453-4ea6-86d7-59dbdcb19666", label: "Diät · Haider", kind: "expense" },
];

function daysInMonth(month?: string): number {
  if (!month) return 31;
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return 31;
  return new Date(y, m, 0).getDate();
}

const gridUrlFor = (id: string) =>
  new URL(`api/cards/timesheet/${id}/grid-bboxes`, document.baseURI).toString();

export function WerkstattGridAuditPage({ modal }: { modal?: boolean } = {}) {
  const nav = useNavigate();
  const [cardId, setCardId] = useState(DEMO[0]!.id);
  const [kind, setKind] = useState<Kind>("timesheet");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [cells, setCells] = useState<AuditCell[]>([]);
  const [headline, setHeadline] = useState("");
  const [expRotate, setExpRotate] = useState(0);

  // Auswahl: gepinnt (Tabellen-Klick / Resolve) + Hover (über Box/Zeile).
  const [sel, setSel] = useState<string | null>(null);
  const [hoverUuid, setHoverUuid] = useState<string | null>(null);
  const [resolveInput, setResolveInput] = useState("");
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  const loadTimesheet = async (id: string) => {
    const card = (await api.timesheets.get(id)) as any;
    const days = daysInMonth(card?.month);
    // GLEICHES Gate wie PdfWithHighlights: nur ein vollständiges 31-Zeilen-Raster
    // mit ≥4 Spalten wird als detected genutzt — sonst Heuristik-Fallback.
    let detected: DetectedGrid | null = null;
    const r = await fetch(gridUrlFor(id), { credentials: "same-origin" });
    if (r.ok) {
      const g = await r.json();
      if (Array.isArray(g.rows) && g.rows.length === 31 && g.cols && Object.keys(g.cols).length >= 4) {
        detected = { rows: g.rows, cols: g.cols };
      }
    }
    const v1 = detectedCellBbox(1, "von1", detected) ?? cellBboxPct(1, "von1");
    const von1Left = v1 ? v1.leftPct : 16;
    const isShifted = !!detected && von1Left > 22;
    const out: AuditCell[] = [];
    for (let day = 1; day <= days; day++) {
      for (const f of TS_FIELDS) {
        const det = detectedCellBbox(day, f, detected);
        const box = det ?? cellBboxPct(day, f);
        const source: Source = det ? "detected" : "heuristic";
        const value = String(card?.data?.days?.[String(day)]?.[f]?.value ?? "");
        let q1: AuditCell["q1"] = "—", q2: AuditCell["q2"] = "—";
        if (box) {
          if (f === "symb") {
            // symb wird SEPARAT erkannt (Header-Geometrie, rechte Bandlage) —
            // erbt NICHT den von1-Versatz. Heuristik = kalibrierter Default ⇒ ok.
            q1 = source === "detected"
              ? (box.leftPct >= 60 && box.leftPct <= 96 ? "ok" : "FALSCHE SPALTE")
              : "ok";
          } else {
            q1 = isShifted ? "FALSCHE SPALTE" : "ok";
          }
          q2 = value && box.widthPct < 5 ? "BOX ZU SCHMAL" : "ok";
        }
        out.push({
          uuid: stepUuid(id, "grid-audit", `${day}_${f}`),
          rowLabel: `Tag ${day}`, field: f, value, box, source, q1, q2,
        });
      }
    }
    setName(card?.last_name ? `${card.last_name}${card.first_name ? ", " + card.first_name : ""}` : "(ohne Name)");
    setHeadline(
      `Stundenliste · Geometrie: ${detected ? "detected-grid" : "Heuristik-Fallback"} · ` +
      `von1 ${von1Left.toFixed(1)} % → ${isShifted ? "SPALTEN-VERSATZ" : "Spalten ok"}`,
    );
    setCells(out);
  };

  const loadExpense = async (id: string) => {
    const r = await fetch(new URL(`api/expenses/${id}`, document.baseURI).toString(), { credentials: "same-origin" });
    if (!r.ok) throw new Error(`expense ${r.status}`);
    const { expense } = await r.json();
    const data = expense?.data ?? {};
    const sp = spatialFromData(data);
    const entries = (data.entries ?? []) as any[];
    const out: AuditCell[] = [];
    entries.forEach((row, i) => {
      for (const f of EXP_FIELDS) {
        const box = sp ? spatialCellBboxPct(sp, i, f) : expenseCellBboxPct(i, f);
        const source: Source = sp ? "spatial" : "heuristic";
        const value = String((row?.[f]?.value ?? row?.[f] ?? "") || "");
        let q1: AuditCell["q1"] = "—", q2: AuditCell["q2"] = "—";
        if (box) {
          // Diäten-Geometrie wird projiziert (spatial) bzw. heuristisch gesetzt,
          // nicht via grid-detect erkannt → Spalten-Versatz hier kein Thema.
          q1 = "ok";
          q2 = value && box.widthPct < 4 ? "BOX ZU SCHMAL" : "ok";
        }
        out.push({
          uuid: stepUuid(id, "grid-audit", `${i}_${f}`),
          rowLabel: `Zeile ${i + 1}`, field: f, value, box, source, q1, q2,
        });
      }
    });
    setName(expense?.last_name ? `${expense.last_name}` : "(Diät)");
    setExpRotate(sp?.rotationAppliedCw ?? 0);
    setHeadline(`Diät · ${entries.length} Zeilen · Geometrie: ${sp ? "spatial (projiziert)" : "Heuristik-Fallback"}`);
    setCells(out);
  };

  const load = useCallback(async (id: string, k: Kind) => {
    setLoading(true); setErr(null); setCells([]); setSel(null); setHoverUuid(null); setKind(k);
    try { k === "timesheet" ? await loadTimesheet(id) : await loadExpense(id); }
    catch (e: any) { setErr(e?.message ?? String(e)); }
    finally { setLoading(false); }
  }, []);

  const resolve = (raw: string) => {
    const id = raw.trim().toLowerCase();
    if (!id) return;
    const hit = cells.find((c) => c.uuid === id);
    if (!hit) { setErr(`ID nicht in dieser Karte gefunden: ${id}`); return; }
    setErr(null);
    setSel(hit.uuid);
    rowRefs.current[hit.uuid]?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  const closeModal = () => { if (modal) nav(-1); else nav("/board"); };
  const previewUrl = kind === "timesheet"
    ? new URL(`api/cards/timesheet/${cardId}/preview-cached`, document.baseURI).toString()
    : new URL(`api/cards/expense/${cardId}/preview?rotate=${expRotate}&dpi=150`, document.baseURI).toString();

  const withBox = cells.filter((c) => c.box).length;
  const q1bad = cells.filter((c) => c.q1 === "FALSCHE SPALTE").length;
  const q2bad = cells.filter((c) => c.q2 === "BOX ZU SCHMAL").length;
  const visibleCells = useMemo(() => cells.filter((c) => c.box), [cells]);

  const activeUuid = hoverUuid ?? sel;
  const activeCell = cells.find((c) => c.uuid === activeUuid) ?? null;

  const inner = (
    <div>
      <WerkstattBranchHeader activeBranch="grid-audit" modal={modal} />
      <div style={{ padding: "8px 16px 24px" }}>
        <h2 style={{ marginTop: 0 }}>Grid-Audit — Highlight pro Zelle</h2>
        <p className="muted" style={{ fontSize: 13, maxWidth: 820 }}>
          Ein Bild: das <strong>Audit-Raster</strong> (alle Zellen-Boxen, eingefärbt nach meinem Urteil) und beim Hover
          das <strong>echte Live-Highlight</strong> (oranges Kästchen wie im Detail-Screen) darübergelegt — so siehst Du,
          ob die markierte Zelle stimmt und ob der Wert vollständig getroffen wird. Jede Zelle hat eine
          {" "}<strong>immutable ID</strong> — kopier sie und sag mir, wo wir nicht dasselbe sehen.
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "10px 0" }}>
          <input value={cardId} onChange={(e) => setCardId(e.target.value.trim())} placeholder="Karten-UUID"
                 style={{ width: 330, padding: "6px 8px", fontFamily: "monospace", fontSize: 12 }} />
          <button className="primary" disabled={loading} onClick={() => load(cardId, kind)}>
            {loading ? "lädt…" : `Audit laden (${kind === "timesheet" ? "Stunden" : "Diät"})`}
          </button>
          <label style={{ fontSize: 12 }}>Typ:
            <select value={kind} onChange={(e) => setKind(e.target.value as Kind)} style={{ marginLeft: 4 }}>
              <option value="timesheet">Stunden</option><option value="expense">Diät</option>
            </select>
          </label>
          {DEMO.map((d) => (
            <button key={d.id} className="link" disabled={loading}
                    onClick={() => { setCardId(d.id); setKind(d.kind); load(d.id, d.kind); }}>{d.label}</button>
          ))}
        </div>
        {err && <div className="error" style={{ marginBottom: 8 }}>{err}</div>}
        {cells.length > 0 && (
          <>
            <div style={{ padding: "8px 12px", borderRadius: 4, marginBottom: 8, fontSize: 13,
                          background: (q1bad || q2bad) ? "#fef2f2" : "#ecfdf5",
                          border: `1px solid ${(q1bad || q2bad) ? "#fca5a5" : "#86efac"}` }}>
              <strong>{name}</strong> · {headline} · falsche Zelle: <strong>{q1bad}/{withBox}</strong> · unvollständig: <strong>{q2bad}/{withBox}</strong>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12, fontSize: 12 }}>
              <input value={resolveInput} onChange={(e) => setResolveInput(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") resolve(resolveInput); }}
                     placeholder="immutable ID einfügen → Zelle finden"
                     style={{ width: 320, padding: "5px 8px", fontFamily: "monospace", fontSize: 11 }} />
              <button className="link" onClick={() => resolve(resolveInput)}>Finden</button>
              {activeCell && (
                <span className="muted">
                  {hoverUuid ? "Hover" : "gewählt"}: <strong>{activeCell.rowLabel} · {activeCell.field}</strong> = {activeCell.value || "leer"}
                  {" "}<code style={{ userSelect: "all" }}>{activeCell.uuid}</code>
                </span>
              )}
            </div>

            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              {/* EIN Bild: Audit-Raster + übergelagertes Live-Highlight */}
              <div style={{ flex: "0 0 auto", width: 620 }}>
                <div style={{ position: "relative", border: "1px solid #ccc" }}>
                  <img src={previewUrl} alt="Scan" style={{ width: "100%", display: "block" }} />
                  {/* Ebene 1: Audit-Raster (wie gehabt) */}
                  {visibleCells.map((c) => {
                    const b = c.box!;
                    const bad = c.q1 === "FALSCHE SPALTE" || c.q2 === "BOX ZU SCHMAL";
                    const col = bad ? "#dc2626" : "#16a34a";
                    return (
                      <div key={c.uuid}
                           title={`${c.rowLabel} · ${c.field} = ${c.value || "leer"} | Q1:${c.q1} Q2:${c.q2} | ${c.source} | ${c.uuid}`}
                           onMouseEnter={() => setHoverUuid(c.uuid)}
                           onMouseLeave={() => setHoverUuid(null)}
                           onClick={() => { setSel(c.uuid); rowRefs.current[c.uuid]?.scrollIntoView({ block: "center" }); }}
                           style={{ position: "absolute", left: `${b.leftPct}%`, top: `${b.topPct}%`,
                                    width: `${b.widthPct}%`, height: `${b.heightPct}%`, cursor: "pointer",
                                    border: `1.5px solid ${col}`,
                                    background: bad ? "rgba(220,38,38,0.10)" : "transparent",
                                    boxSizing: "border-box", zIndex: 1 }} />
                    );
                  })}
                  {/* Ebene 2: echtes Live-Highlight (orange, .pdf-overlay-cell.active-Stil), übergelagert */}
                  {activeCell?.box && (
                    <div aria-hidden style={{ position: "absolute", left: `${activeCell.box.leftPct}%`, top: `${activeCell.box.topPct}%`,
                                              width: `${activeCell.box.widthPct}%`, height: `${activeCell.box.heightPct}%`,
                                              border: "1.5px solid #f59e0b", borderRadius: 2,
                                              background: "rgba(245,158,11,0.30)", boxShadow: "0 0 0 2px rgba(245,158,11,0.35)",
                                              boxSizing: "border-box", pointerEvents: "none", zIndex: 5 }} />
                  )}
                </div>
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                  Box = exakt die Produktions-Geometrie (detectedCellBbox ?? cellBboxPct · spatialCellBboxPct ?? expenseCellBboxPct).
                  <span style={{ color: "#16a34a" }}> grün ok</span> · <span style={{ color: "#dc2626" }}>rot Problem</span> · <span style={{ color: "#f59e0b" }}>orange = Live-Highlight (Hover)</span>.
                </div>
              </div>

              {/* Tabelle: jede Zelle mit Quelle, Urteil, immutable ID */}
              <div style={{ flex: "1 1 460px", maxHeight: 760, overflow: "auto" }}>
                <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
                  <thead><tr style={{ position: "sticky", top: 0, background: "#f8fafc" }}>
                    <th style={thtd}>Zelle</th><th style={thtd}>Feld</th><th style={thtd}>Wert</th>
                    <th style={thtd}>Quelle</th><th style={thtd}>richtige Spalte?</th>
                    <th style={thtd}>vollständig?</th><th style={thtd}>ID (immutable)</th>
                  </tr></thead>
                  <tbody>
                    {visibleCells.map((c) => (
                      <tr key={c.uuid} ref={(el) => { rowRefs.current[c.uuid] = el; }}
                          onClick={() => setSel(c.uuid)}
                          onMouseEnter={() => setHoverUuid(c.uuid)}
                          onMouseLeave={() => setHoverUuid(null)}
                          style={{ background: c.uuid === activeUuid ? "#fef3c7" : c.uuid === sel ? "#dbeafe" : undefined, cursor: "pointer" }}>
                        <td style={thtd}>{c.rowLabel}</td>
                        <td style={thtd}><code>{c.field}</code></td>
                        <td style={thtd}>{c.value || "—"}</td>
                        <td style={{ ...thtd, color: c.source === "heuristic" ? "#b45309" : "#1e3a8a" }}>{c.source}</td>
                        <td style={{ ...thtd, color: c.q1 === "ok" ? "#15803d" : "#b91c1c", fontWeight: c.q1 === "ok" ? 400 : 700 }}>{c.q1}</td>
                        <td style={{ ...thtd, color: c.q2 === "ok" ? "#15803d" : "#b91c1c", fontWeight: c.q2 === "ok" ? 400 : 700 }}>{c.q2}</td>
                        <td style={{ ...thtd, fontFamily: "monospace", fontSize: 11, userSelect: "all" }}>{c.uuid}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
  if (modal) return <DetailModal dirty={false} onClose={closeModal}>{inner}</DetailModal>;
  return inner;
}

const thtd: React.CSSProperties = { border: "1px solid #e2e8f0", padding: "2px 7px", textAlign: "left", verticalAlign: "top" };
